import {HttpEventType, HttpResponse, HttpUploadProgressEvent} from '@angular/common/http';
import {Component, forwardRef, Input, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BucketName, FileAttachmentApiService, FileUploadResponse} from '@npck/af-frontend-api';
import {BusinessErrorCode, downloadFromBlobResponse, rxSpinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {NgxDropzoneChangeEvent, NgxDropzoneComponent} from 'ngx-dropzone';
import {catchError, EMPTY, forkJoin, map, mergeMap, of, takeWhile} from 'rxjs';
import {fromIterable} from 'rxjs/internal/observable/innerFrom';
import {v4 as uuidV4} from 'uuid';
import {MAX_FILE_SIZE, MAX_FILES_COUNT} from '../../domain/constants';

@Component({
    selector: 'af-drop-zone',
    templateUrl: './drop-zone-controller.component.html',
    styleUrl: './drop-zone-controller.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropZoneControllerComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class DropZoneControllerComponent implements ControlValueAccessor {
    @ViewChild('dropzone') dropzone: NgxDropzoneComponent;

    @Input() mimeTypes: string[] = ['*'];
    @Input() maxFileSize: number = MAX_FILE_SIZE;
    @Input() mode: DropZoneMode = 'drop-zone';
    @Input() label: string;
    @Input() hint: string;
    @Input() multiple = false;
    @Input() showPreview = true;
    @Input() hasMargin: boolean = true;
    @Input() dropZoneStyle: DropZoneStyle = 'light';
    @Input() bucket: BucketName;
    @Input() hasGridUploadedDocContainer: boolean = true;
    @Input() downloadable = false;
    canceledFiles: string[] = [];
    hasError = false;

    disabled = false;
    private selectedFiles: FileData[] = [];

    constructor(
        private fileAttachmentApiService: FileAttachmentApiService,
        private snackbarService: SnackbarService,
    ) {
    }

    get nonDeletedFiles(): FileData[] {
        return this.selectedFiles?.filter(file => !file.isDeleted) || [];
    }

    onFilesChanged(selectedFilesEvent: NgxDropzoneChangeEvent): void {
        if (selectedFilesEvent.rejectedFiles.length > 0) {
            const hasSize = selectedFilesEvent.rejectedFiles.some(r => r.reason === 'size');
            const hasType = selectedFilesEvent.rejectedFiles.some(r => r.reason === 'type');
            if (hasSize) {
                this.snackbarService.showSnackBar('i18n_af_errorUploadMaxFileSize', 'error');
            }
            if (hasType) {
                this.snackbarService.showSnackBar('i18n_af_errorUploadMimeType', 'error');
            }
            this.hasError = true;
        }

        const selectedFiles = this.multiple
            ? selectedFilesEvent.addedFiles
            : selectedFilesEvent.addedFiles.slice(0, 1);
        if (!this.selectedFiles) {
            this.selectedFiles = [];
        }
        if (!selectedFiles.length) {
            return;
        }

        const currentFiles = this.selectedFiles?.filter(f => !f.isDeleted) || [];
        if (currentFiles.length + selectedFiles.length > MAX_FILES_COUNT) {
            this.snackbarService.showSnackBar('i18n_af_errorUploadMaxFilesCount', 'error');
            this.hasError = true;
            return;
        }

        this.hasError = false;

        forkJoin(selectedFiles.map(item => of(item)))
            .pipe(
                map(contents => contents.map(content => {
                    return <FileData>{
                        tempId: uuidV4(),
                        content: content,
                        fileName: content.name,
                        hover: false,
                        completed: false,
                        progressLoaded: 0,
                        progressTotal: content.size,
                    };
                })),
                mergeMap(filesToUpload => {
                    if (this.multiple) {
                        this.selectedFiles.push(...filesToUpload);
                    } else {
                        this.selectedFiles = filesToUpload;
                    }
                    return fromIterable(filesToUpload)
                        .pipe(
                            mergeMap(fileToUpload =>
                                this.fileAttachmentApiService.uploadFile(this.bucket, fileToUpload.content, 'events', true)
                                    .pipe(
                                        takeWhile(() => !this.canceledFiles.includes(fileToUpload.tempId)),
                                        mergeMap(event => {
                                            if (event.type === HttpEventType.UploadProgress) {
                                                const uploadEvent = event as HttpUploadProgressEvent;
                                                fileToUpload.progressLoaded = uploadEvent.loaded / (uploadEvent.total / 100);
                                            } else if (event.type === HttpEventType.Response) {
                                                const responseEvent = event as HttpResponse<FileUploadResponse>;
                                                fileToUpload.progressLoaded = 0;
                                                fileToUpload.completed = true;
                                                fileToUpload.id = responseEvent.body.id;
                                            }
                                            return EMPTY;
                                        }),
                                        catchError(err => {
                                            if (err?.error?.code === BusinessErrorCode.AF_FILE_HAS_VIRUS
                                                || err?.error?.errorCode === BusinessErrorCode.AF_FILE_HAS_VIRUS
                                            ) {
                                                this.snackbarService.showSnackBar('i18n_af_errorUploadVirus', 'error');
                                                this.hasError = true;
                                            } else if (err?.error?.code === BusinessErrorCode.AF_FILE_WRONG_EXTENSION
                                                || err?.error?.errorCode === BusinessErrorCode.AF_FILE_WRONG_EXTENSION
                                            ) {
                                                this.snackbarService.showSnackBar('i18n_af_errorWrongExtensions', 'error');
                                                this.hasError = true;
                                            }
                                            this.deleteFile(fileToUpload);
                                            throw err;
                                        }),
                                    ),
                            ));
                }),
            )
            .subscribe({
                complete: () => {
                    this.onChange(this.selectedFiles);
                    this.onTouched();
                },
            });
    }

    openPicker(): void {
        this.dropzone.showFileSelector();
    }

    clearFiles(): void {
        this.selectedFiles = null;
        this.onChange(null);
        this.onTouched();
    }

    writeValue(value: FileData[] | null): void {
        if (!value) {
            this.clearFiles();
        } else {
            this.selectedFiles = [...value];
        }
    }

    registerOnChange(fn: (value: FileData[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    deleteFile(fileData: FileData): void {
        if (fileData.id) {
            const file = this.selectedFiles.find(fileItem => fileItem.id === fileData.id);
            file.isDeleted = true;
        } else {
            this.canceledFiles.push(fileData.tempId);
            const index = this.selectedFiles.findIndex(fileItem => fileItem.tempId === fileData.tempId);
            this.selectedFiles.splice(index, 1);
        }
        this.onChange(this.selectedFiles);
        this.onTouched();
    }

    isDownloadable(file: FileData): boolean {
        return this.downloadable && !!file.id && !file.content;
    }

    downloadFile(file: FileData): void {
        if (!this.isDownloadable(file)) {
            return;
        }
        this.fileAttachmentApiService.downloadFile(file.id!, 'response')
            .pipe(rxSpinner(`downloadFile_${file.id}`))
            .subscribe(response => downloadFromBlobResponse(response));
    }

    private onChange: (value: FileData[] | null) => void = () => {
    };
    private onTouched: () => void = () => {
    };
}

export declare type DropZoneStyle = 'dark' | 'light';
export declare type DropZoneMode = 'drop-zone' | 'button';

export interface FileData {
    id?: string;
    tempId: string;
    content?: Blob;
    fileName: string;
    completed?: boolean;
    hover?: boolean;
    progressLoaded?: number;
    progressTotal?: number;
    isDeleted?: boolean;
}
